import { ProductStoreProduct } from "../../api/fetchProductStoreProducts/types";
import { Column } from "@/components/Datatable/types";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Edit, Trash2 } from "lucide-react";
import { getProductTypeBadgeConfig } from "../../utils/productTypeBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function createProductColumns(
  onEdit: (product: ProductStoreProduct) => void,
  onDelete: (product: ProductStoreProduct) => void,
  onTogglePublished: (product: ProductStoreProduct) => void,
): Column<ProductStoreProduct>[] {
  return [
    {
      header: "Name",
      accessorKey: "name",
      enableSorting: true,
    },
    {
      header: "Code",
      accessorKey: "code",
      enableSorting: true,
    },
    {
      header: "Type",
      accessorKey: "type",
      cell: ({ row }) => {
        const config = getProductTypeBadgeConfig(row.original.type);
        const Icon = config.icon;
        return (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${config.style}`}
          >
            <Icon className="h-3 w-3" />
            {config.label}
          </span>
        );
      },
    },
    {
      header: "Category",
      accessorKey: "category",
      cell: ({ row }) => {
        const category = row.original.category;
        if (!category) return "--";
        return (
          <Badge
            className="whitespace-nowrap text-white"
            style={{
              backgroundColor: category.color?.value || "#6B7280",
            }}
            variant="secondary"
          >
            {category.name}
          </Badge>
        );
      },
    },
    {
      header: "Price",
      accessorKey: "price",
      cell: ({ row }) => (
        <span className="text-gray-900 dark:text-white">
          ${parseFloat(row.original.price).toFixed(2)}
        </span>
      ),
      enableSorting: true,
    },
    {
      header: "Published",
      accessorKey: "isPublished",
      cell: ({ row }) => (
        <Switch
          checked={row.original.isPublished}
          onCheckedChange={() => onTogglePublished(row.original)}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-8 w-8 p-0 hover:bg-gray-200 transition-colors"
              variant="ghost"
            >
              <span className="sr-only">Open menu</span>
              <EllipsisVerticalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[150px] p-2 bg-white">
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => onEdit(row.original)}
            >
              <Edit className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 transition-colors"
              onClick={() => onDelete(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];
}
